import React, { useCallback } from 'react';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import {
  WELCOME_LETTER_BODY_MAX_LENGTH,
  WELCOME_LETTER_BODY_MIN_LENGTH,
} from "../../../DefaultWelcomeScreen/welcomeLetterHtmlUtils";
import {
  insertPlainTextWithMaxLength,
  getProjectedPlainTextLengthAfterInsert,
  shouldAcceptWelcomeLetterPlainTextLengthChange,
} from "../../../DefaultWelcomeScreen/welcomeLetterEditorUtils";

const EditorConvertToHTML = ({
  editorState,
  setEditorState,
  setHtmlDraftError,
  maxLength = WELCOME_LETTER_BODY_MAX_LENGTH,
}) => {
  const validatePlainText = useCallback(
    (text) => {
      if (!text.trim()) {
        setHtmlDraftError('This field is mandatory');
        return;
      }
      if (text.length < WELCOME_LETTER_BODY_MIN_LENGTH) {
        setHtmlDraftError(`Welcome text must be at least ${WELCOME_LETTER_BODY_MIN_LENGTH} characters`);
        return;
      }
      if (text.length > maxLength) {
        setHtmlDraftError(`Welcome text must not exceed ${maxLength} characters`);
        return;
      }
      setHtmlDraftError('');
    },
    [maxLength, setHtmlDraftError]
  );

  const onEditorHandler = useCallback(
    (state) => {
      setEditorState((prev) => {
        const prevLength = prev.getCurrentContent().getPlainText().length;
        const nextLength = state.getCurrentContent().getPlainText().length;

        if (
          !shouldAcceptWelcomeLetterPlainTextLengthChange(
            prevLength,
            nextLength,
            maxLength
          )
        ) {
          validatePlainText(prev.getCurrentContent().getPlainText());
          return prev;
        }

        validatePlainText(state.getCurrentContent().getPlainText());
        return state;
      });
    },
    [maxLength, setEditorState, validatePlainText]
  );

  const handleBeforeInput = useCallback(
    (chars, state) => {
      const currentLength = state.getCurrentContent().getPlainText().length;
      const projected = getProjectedPlainTextLengthAfterInsert(state, chars);

      if (
        !shouldAcceptWelcomeLetterPlainTextLengthChange(
          currentLength,
          projected,
          maxLength
        )
      ) {
        return 'handled';
      }
      return 'not-handled';
    },
    [maxLength]
  );

  const handlePastedText = useCallback(
    (text, _html, state, onChange) => {
      const nextState = insertPlainTextWithMaxLength(state, text, maxLength);

      if (!nextState) {
        validatePlainText(state.getCurrentContent().getPlainText());
        return true;
      }

      onChange(nextState);
      return true;
    },
    [maxLength, validatePlainText]
  );

  return (
    <Editor
      editorState={editorState}
      wrapperClassName="html-editor-wrapper"
      editorClassName="html-editor-welcome-screen"
      onEditorStateChange={onEditorHandler}
      handleBeforeInput={handleBeforeInput}
      handlePastedText={handlePastedText}
      placeholder="Welcome letter text content..."
      toolbar={{
        fontSize: {
          options: [12, 14, 16],
        },
      }}
    />
  );
};

export default EditorConvertToHTML;
